/**
 * TradeModal.ts - DAoC Style 2-Player / Companion Trade Window
 * 
 * - Offers 4x2 offer slots on player's side and partner's side
 * - Bottom frame displays offered currency (P / G / S / C)
 * - Trade status: 'offering' -> 'accepted' (lock) -> confirmed when both accept
 */

import { EntityState, InventoryItem } from '../../core/Types';
import { Currency } from '../../core/Currency';
import { EventBus } from '../../core/EventBus';
import { addItemToInventory } from '../character/CharacterSystem';

export class TradeModal {
  private el: HTMLElement | null = null;
  private isOpen = false;

  private player: EntityState | null = null;
  private partner: EntityState | null = null;

  // Offered currencies
  private playerOfferedCopper = 0;
  private partnerOfferedCopper = 0;

  // Accepted statuses
  private playerAccepted = false;
  private partnerAccepted = false;

  // Offered items (up to 4 items each)
  private playerItems: Array<InventoryItem | null> = [null, null, null, null];
  private partnerItems: Array<InventoryItem | null> = [null, null, null, null];

  constructor() {
    this.createDom();
  }

  private createDom(): void {
    if (document.getElementById('daoc-trade-modal')) return;

    const wrap = document.createElement('div');
    wrap.id = 'daoc-trade-modal';
    wrap.style.cssText = `
      position: fixed;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 480px;
      background: linear-gradient(180deg, #2b2218, #18120b);
      border: 2px solid #7a5c2e;
      border-radius: 6px;
      box-shadow: 0 0 0 1px #000, inset 0 0 0 1px #c9a45a44, 0 12px 30px rgba(0,0,0,0.8);
      color: #e8dcc0;
      font-family: sans-serif;
      z-index: 10000;
      display: none;
      flex-direction: column;
      user-select: none;
    `;

    document.body.appendChild(wrap);
    this.el = wrap;
  }

  public open(player: EntityState, partner: EntityState, initialCopper: number = 0): void {
    this.player = player;
    this.partner = partner;
    this.playerOfferedCopper = Math.max(0, Math.min(player.gold ?? 0, initialCopper));
    this.partnerOfferedCopper = 0;
    this.playerAccepted = false;
    this.partnerAccepted = false;
    this.playerItems = [null, null, null, null];
    this.partnerItems = [null, null, null, null];

    this.isOpen = true;
    this.render();
  }

  public close(): void {
    this.isOpen = false;
    if (this.el) {
      this.el.style.display = 'none';
      this.el.innerHTML = '';
    }
  }

  private render(): void {
    if (!this.el || !this.player || !this.partner) return;
    this.el.style.display = 'flex';

    const pName = this.player.name;
    const tName = this.partner.name;

    const pBreakdown = Currency.breakdown(this.playerOfferedCopper);
    const tBreakdown = Currency.breakdown(this.partnerOfferedCopper);

    this.el.innerHTML = `
      <!-- DAoC Style Titlebar -->
      <div style="background:linear-gradient(#5a431f,#3a2a12); border-bottom:1px solid #000; padding:6px 12px; display:flex; justify-content:space-between; align-items:center; font-size:12px; font-weight:bold; color:#f3d98b; letter-spacing:0.5px;">
        <span style="display:flex; align-items:center; gap:6px;">
          <span>⚖️</span>
          <span>거래 교환 창 (DAoC Trade Window)</span>
        </span>
        <button id="btn-trade-close" style="background:none; border:none; color:#f3d98b; font-size:14px; cursor:pointer; font-weight:bold;">✕</button>
      </div>

      <!-- Trade Columns -->
      <div style="padding:12px; display:grid; grid-template-columns:1fr 1fr; gap:12px; background:rgba(0,0,0,0.25);">
        
        <!-- Left: Player Column -->
        <div style="background:rgba(20,15,10,0.65); border:1.5px solid ${this.playerAccepted ? '#22c55e' : '#5a431f'}; border-radius:4px; padding:10px;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; font-size:11.5px; font-weight:bold;">
            <span style="color:#38bdf8;">${this.player.avatarIcon || '👤'} ${pName}</span>
            <span style="color:${this.playerAccepted ? '#4ade80' : '#94a3b8'}; font-size:10px;">${this.playerAccepted ? '✔ 수락 대기' : '제시 중'}</span>
          </div>

          <!-- 4 Offer Item Slots -->
          <div style="display:grid; grid-template-columns:repeat(4, 1fr); gap:4px; margin-bottom:10px;">
            ${this.playerItems.map((item, idx) => `
              <div style="height:48px; background:#0c0905; border:1px solid #3f2f17; border-radius:3px; display:flex; align-items:center; justify-content:center; font-size:16px;">
                ${item ? item.icon : ''}
              </div>
            `).join('')}
          </div>

          <!-- Offered Money Frame (창틀) -->
          <div style="background:#0c0905; border:1.5px inset #5a431f; border-radius:3px; padding:6px 8px; font-size:11px;">
            <div style="font-size:9.5px; color:#cdb98a; margin-bottom:3px; font-weight:bold;">제시한 금액:</div>
            <div style="display:flex; justify-content:space-between; align-items:center; gap:4px; font-family:monospace; font-size:11px;">
              <span style="color:#a78bfa;" title="플래티넘">🪙${pBreakdown.platinum}<small>P</small></span>
              <span style="color:#fde047;" title="골드">🪙${pBreakdown.gold}<small>G</small></span>
              <span style="color:#cbd5e1;" title="실버">🪙${pBreakdown.silver}<small>S</small></span>
              <span style="color:#f97316;" title="코퍼">🪙${pBreakdown.copper}<small>C</small></span>
            </div>
            <div style="text-align:right; font-size:9.5px; color:#4ade80; margin-top:3px; font-weight:bold;">
              총 ${Currency.format(this.playerOfferedCopper)}
            </div>
          </div>
        </div>

        <!-- Right: Partner Column -->
        <div style="background:rgba(20,15,10,0.65); border:1.5px solid ${this.partnerAccepted ? '#22c55e' : '#5a431f'}; border-radius:4px; padding:10px;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; font-size:11.5px; font-weight:bold;">
            <span style="color:#fde047;">${this.partner.avatarIcon || '👤'} ${tName}</span>
            <span style="color:${this.partnerAccepted ? '#4ade80' : '#94a3b8'}; font-size:10px;">${this.partnerAccepted ? '✔ 수락 대기' : '검토 중'}</span>
          </div>

          <!-- 4 Offer Item Slots -->
          <div style="display:grid; grid-template-columns:repeat(4, 1fr); gap:4px; margin-bottom:10px;">
            ${this.partnerItems.map((item, idx) => `
              <div style="height:48px; background:#0c0905; border:1px solid #3f2f17; border-radius:3px; display:flex; align-items:center; justify-content:center; font-size:16px;">
                ${item ? item.icon : ''}
              </div>
            `).join('')}
          </div>

          <!-- Offered Money Frame (창틀) -->
          <div style="background:#0c0905; border:1.5px inset #5a431f; border-radius:3px; padding:6px 8px; font-size:11px;">
            <div style="font-size:9.5px; color:#cdb98a; margin-bottom:3px; font-weight:bold;">상대방 제시 금액:</div>
            <div style="display:flex; justify-content:space-between; align-items:center; gap:4px; font-family:monospace; font-size:11px;">
              <span style="color:#a78bfa;" title="플래티넘">🪙${tBreakdown.platinum}<small>P</small></span>
              <span style="color:#fde047;" title="골드">🪙${tBreakdown.gold}<small>G</small></span>
              <span style="color:#cbd5e1;" title="실버">🪙${tBreakdown.silver}<small>S</small></span>
              <span style="color:#f97316;" title="코퍼">🪙${tBreakdown.copper}<small>C</small></span>
            </div>
            <div style="text-align:right; font-size:9.5px; color:#4ade80; margin-top:3px; font-weight:bold;">
              총 ${Currency.format(this.partnerOfferedCopper)}
            </div>
          </div>
        </div>

      </div>

      <!-- Footer Action Area -->
      <div style="background:linear-gradient(#2b2218,#1b150e); border-top:1px solid #5a431f; padding:10px 14px; display:flex; justify-content:space-between; align-items:center;">
        <div style="font-size:10.5px; color:#cdb98a;">
          상태: <b style="color:${this.playerAccepted ? '#4ade80' : '#fde047'};">${this.playerAccepted ? '상대방의 동의를 기다리는 중...' : '금액 및 물품을 확인 후 거래 수락'}</b>
        </div>
        <div style="display:flex; gap:8px;">
          <button id="btn-trade-cancel" style="background:#450a0a; border:1px solid #ef4444; color:#fff; padding:6px 12px; border-radius:4px; font-size:11px; font-weight:bold; cursor:pointer;">
            거래 취소
          </button>
          <button id="btn-trade-accept" style="background:${this.playerAccepted ? '#15803d' : '#0284c7'}; border:1px solid ${this.playerAccepted ? '#86efac' : '#38bdf8'}; color:#fff; padding:6px 16px; border-radius:4px; font-size:11px; font-weight:bold; cursor:pointer;">
            ${this.playerAccepted ? '✔ 수락됨 (대기)' : '거래 수락'}
          </button>
        </div>
      </div>
    `;

    this.bindEvents();
  }

  private bindEvents(): void {
    if (!this.el) return;

    this.el.querySelector('#btn-trade-close')?.addEventListener('click', () => this.cancelTrade());
    this.el.querySelector('#btn-trade-cancel')?.addEventListener('click', () => this.cancelTrade());

    this.el.querySelector('#btn-trade-accept')?.addEventListener('click', () => {
      this.playerAccepted = !this.playerAccepted;
      this.render();

      if (this.playerAccepted) {
        // If partner is an NPC or Companion AI, automatically accept after 0.5s
        setTimeout(() => {
          if (!this.isOpen) return;
          this.partnerAccepted = true;
          this.completeTrade();
        }, 500);
      }
    });
  }

  private completeTrade(): void {
    if (!this.player || !this.partner) return;

    const p = this.player;
    const t = this.partner;

    const pGold = p.gold ?? 0;
    const tGold = t.gold ?? 0;

    if (pGold < this.playerOfferedCopper) {
      alert('보유한 금액이 부족하여 거래가 취소되었습니다.');
      this.cancelTrade();
      return;
    }

    // Execute transfer
    p.gold = pGold - this.playerOfferedCopper + this.partnerOfferedCopper;
    t.gold = tGold + this.playerOfferedCopper - this.partnerOfferedCopper;

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: `🤝 [거래 체결 완료] [${p.name}] ➔ [${t.name}] 에게 ${Currency.format(this.playerOfferedCopper)} 전달 완료!`,
      category: 'system',
    });

    this.close();
  }

  private cancelTrade(): void {
    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: `🚫 거래가 취소되었습니다.`,
      category: 'system',
    });
    this.close();
  }
}

export const tradeModalInstance = new TradeModal();
